import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLSeoBasics = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">SEO & Discovery</div>
                <h1>HTML SEO Basics</h1>
                <p className="hero-lead">
                    Optimize your HTML for search engines. Learn how proper HTML structure, meta tags,
                    and semantic elements improve your search engine rankings and discoverability.
                </p>
            </div>

            <div className="content-section">
                <h2>Why HTML Matters for SEO</h2>
                <p>
                    Search engines rely heavily on HTML structure to understand and rank your content.
                    Proper HTML is the foundation of good SEO.
                </p>

                <div className="seo-factors">
                    <h3>HTML Elements That Impact SEO:</h3>
                    <ul>
                        <li>Title tags</li>
                        <li>Meta descriptions</li>
                        <li>Heading hierarchy (H1-H6)</li>
                        <li>Semantic structure</li>
                        <li>Image alt text</li>
                        <li>Internal linking</li>
                        <li>URL structure</li>
                        <li>Page load speed (HTML optimization)</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Title Tags - The Most Important SEO Element</h2>

                <h3>Perfect Title Tag Structure</h3>
                <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <!-- 50-60 characters optimal --&gt;
    <title>HTML Tutorial - Complete Guide for Beginners | YourSite</title>
    
    <!-- Formula: Primary Keyword | Brand --&gt;
    <!-- OR: Primary Keyword - Secondary Keyword | Brand --&gt;
</head>`} />

                <h3>Title Tag Best Practices</h3>
                <ul>
                    <li><strong>Length:</strong> 50-60 characters (512 pixels in search results)</li>
                    <li><strong>Keywords:</strong> Include primary keyword near the beginning</li>
                    <li><strong>Unique:</strong> Every page should have unique title</li>
                    <li><strong>Descriptive:</strong> Accurately describe page content</li>
                    <li><strong>Brand:</strong> Include brand name (usually at end)</li>
                    <li><strong>Separators:</strong> Use | or - to separate parts</li>
                </ul>

                <h3>Title Tag Examples</h3>
                <div className="code-header"><span>Good Title Tags</span></div>
                <CodeBlock language="html" code={`<!-- Homepage --&gt;
<title>FileFusion - Free Online File Converter & PDF Tools</title>

<!-- Tool Page --&gt;
<title>Free Image Compressor - Reduce JPG, PNG Size | FileFusion</title>

<!-- Tutorial Page --&gt;
<title>How to Compress Images Without Losing Quality - 2024 Guide</title>

<!-- Blog Post --&gt;
<title>10 Best Image Formats for Web Performance | FileFusion Blog</title>`} />

                <div className="code-header"><span> Bad Title Tags</span></div>
                <CodeBlock className="error" language="html" code={`<!-- Too generic --&gt;
<title>Home</title>

<!-- Too long (truncated in search results) --&gt;
<title>Free Online Image Compressor Tool to Reduce JPG PNG WebP File Size Without Losing Quality</title>

<!-- Keyword stuffing --&gt;
<title>Image Compressor | Compress Images | Image Compression Tool | Reduce Image Size</title>

<!-- Missing brand --&gt;
<title>Convert PDF to Word</title>

<!-- Duplicate across multiple pages --&gt;
<title>FileFusion Tools</title>`} />
            </div>

            <div className="content-section">
                <h2>Meta Descriptions</h2>

                <h3>Perfect Meta Description</h3>
                <CodeBlock language="html" code={`<head>
    <title>Free Image Compressor - Reduce JPG, PNG Size | FileFusion</title>
    
    <!-- 150-160 characters optimal --&gt;
    <meta name="description" content="Compress images online for free. Reduce JPG, PNG, and WebP file sizes by up to 80% without losing quality. Fast, secure, and no registration required.">
</head>`} />

                <h3>Meta Description Best Practices</h3>
                <ul>
                    <li><strong>Length:</strong> 150-160 characters (920 pixels)</li>
                    <li><strong>Keywords:</strong> Include target keywords naturally</li>
                    <li><strong>Call-to-action:</strong> Encourage clicks</li>
                    <li><strong>Unique:</strong> Every page should have unique description</li>
                    <li><strong>Accurate:</strong> Must match actual page content</li>
                    <li><strong>Benefits:</strong> Highlight what users will get</li>
                </ul>

                <div className="code-header"><span>Good Meta Descriptions</span></div>
                <CodeBlock language="html" code={`<!-- Homepage --&gt;
<meta name="description" content="Free online tools for file conversion, image compression, PDF editing, and more. Fast, secure, and no registration required. Try FileFusion today!">

<!-- Tool Page --&gt;
<meta name="description" content="Remove backgrounds from images automatically using AI. Upload JPG or PNG files, get transparent PNG results in seconds. 100% free with no watermarks.">

<!-- Tutorial --&gt;
<meta name="description" content="Learn how JPEG compression works. Understand DCT, quantization, and entropy coding. Complete guide with visual examples and practical tips.">

<!-- Blog Post --&gt;
<meta name="description" content="Discover the best image formats for web performance in 2024. Compare JPEG, PNG, WebP, and AVIF. Includes compression tips and use cases.">`} />
            </div>

            <div className="content-section">
                <h2>Heading Hierarchy for SEO</h2>

                <h3>Perfect Heading Structure</h3>
                <CodeBlock language="html" code={`<body>
    <!-- One H1 per page (main topic) --&gt;
    <h1>Complete Guide to Image Compression</h1>
    
    <!-- H2 for main sections --&gt;
    <h2>What is Image Compression?</h2>
    <p>Content...</p>
    
    <!-- H3 for subsections --&gt;
    <h3>Lossy vs Lossless Compression</h3>
    <p>Content...</p>
    
    <h3>Common Compression Algorithms</h3>
    <p>Content...</p>
    
    <h2>JPEG Compression</h2>
    <p>Content...</p>
    
    <h3>How JPEG Works</h3>
    <p>Content...</p>
    
    <h3>JPEG Quality Settings</h3>
    <p>Content...</p>
</body>`} />

                <h3>Heading SEO Rules</h3>
                <ul>
                    <li> <strong>One H1:</strong> Each page should have exactly one H1</li>
                    <li> <strong>H1 = Page topic:</strong> H1 should match page title/topic</li>
                    <li> <strong>Keywords:</strong> Include primary keyword in H1</li>
                    <li> <strong>Hierarchy:</strong> Don't skip levels (H1  H3)</li>
                    <li> <strong>Descriptive:</strong> Headings should describe content</li>
                    <li> <strong>Natural:</strong> Write for humans, not just search engines</li>
                </ul>

                <div className="code-header"><span> Common Heading Mistakes</span></div>
                <CodeBlock className="error" language="html" code={`<!-- Multiple H1s --&gt;
<h1>Welcome</h1>
<h1>Our Services</h1>  <!--  Only one H1 per page --&gt;

<!-- Skipping levels --&gt;
<h1>Main Title</h1>
<h4>Subsection</h4>  <!--  Skipped H2 and H3 --&gt;

<!-- Using headings for styling only --&gt;
<h3>Small text</h3>  <!--  Use CSS, not heading levels --&gt;

<!-- Keyword stuffing --&gt;
<h1>Image Compressor Tool | Compress Images | Image Compression</h1>`} />
            </div>

            <div className="content-section">
                <h2>Semantic HTML for SEO</h2>

                <h3>Search Engines Love Semantic HTML</h3>
                <CodeBlock language="html" code={`<body>
    <!-- Clear page structure --&gt;
    <header>
        <nav>
            <a href="/">Home</a>
            <a href="/tools">Tools</a>
        </nav>
    </header>
    
    <main>
        <article>
            <h1>Article Title</h1>
            
            <section>
                <h2>Introduction</h2>
                <p>Content...</p>
            </section>
            
            <section>
                <h2>Main Content</h2>
                <p>Content...</p>
            </section>
        </article>
        
        <aside>
            <h2>Related Articles</h2>
            <!-- Sidebar content --&gt;
        </aside>
    </main>
    
    <footer>
        <!-- Footer content --&gt;
    </footer>
</body>

<!-- Search engines understand:
   - Main content vs navigation
   - Articles vs sidebars
   - Page structure and hierarchy --&gt;`} />
            </div>

            <div className="content-section">
                <h2>Image SEO</h2>

                <h3>Optimized Image HTML</h3>
                <CodeBlock language="html" code={`<!-- Descriptive alt text (5-15 words) --&gt;
<img src="image-compression-comparison.jpg" 
     alt="Before and after comparison showing 70% file size reduction with no visible quality loss"
     width="800" 
     height="600"
     loading="lazy">

<!-- For decorative images --&gt;
<img src="decoration.png" alt="">

<!-- Image in figure with caption --&gt;
<figure>
    <img src="compression-chart.jpg" 
         alt="Bar chart comparing JPEG, PNG, and WebP compression ratios">
    <figcaption>
        Compression efficiency comparison across three formats
    </figcaption>
</figure>`} />

                <h3>Image SEO Checklist</h3>
                <ul>
                    <li> Descriptive alt text (not keyword stuffing)</li>
                    <li> Descriptive filenames (compression-guide.jpg, not img123.jpg)</li>
                    <li> Appropriate file format (JPEG for photos, PNG for graphics)</li>
                    <li> Optimized file size (compress images)</li>
                    <li> Width and height attributes (prevent layout shift)</li>
                    <li> Lazy loading for below-fold images</li>
                    <li> Responsive images (srcset for different sizes)</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Internal Linking</h2>

                <h3>Strategic Internal Links</h3>
                <CodeBlock language="html" code={`<article>
    <h1>How to Compress Images for Web</h1>
    
    <p>
        Image compression is essential for web performance. 
        Learn about 
        <a href="/insights/courses/image-formats">different image formats</a> 
        and how to choose the right one.
    </p>
    
    <p>
        Use our 
        <a href="/tools/image-compressor">free image compressor tool</a> 
        to reduce file sizes by up to 80%.
    </p>
    
    <section>
        <h2>Related Tutorials</h2>
        <ul>
            <li><a href="/insights/courses/jpeg-compression">JPEG Compression Guide</a></li>
            <li><a href="/insights/courses/png-optimization">PNG Optimization Tips</a></li>
            <li><a href="/insights/courses/webp-guide">WebP Format Explained</a></li>
        </ul>
    </section>
</article>`} />

                <h3>Internal Linking Best Practices</h3>
                <ul>
                    <li>Use descriptive anchor text (not "click here")</li>
                    <li>Link to relevant related content</li>
                    <li>Link from high-authority pages to important pages</li>
                    <li>Create logical site structure</li>
                    <li>Use natural, contextual links</li>
                    <li> Don't over-optimize anchor text</li>
                    <li> Don't use exact match keywords repeatedly</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>URL Structure</h2>

                <h3>SEO-Friendly URLs</h3>
                <div className="code-header"><span>Good URLs</span></div>
                <CodeBlock language="bash" code={`https://www.filefusion.app/tools/image-compressor
https://www.filefusion.app/insights/courses/html/seo-basics
https://www.filefusion.app/blog/best-image-formats-2024

<!-- Characteristics:
   - Readable by humans
   - Include keywords
   - Use hyphens (not underscores)
   - Lowercase
   - Short and descriptive --&gt;`} />

                <div className="code-header"><span> Bad URLs</span></div>
                <CodeBlock className="error" language="bash" code={`https://www.filefusion.app/page?id=12345
https://www.filefusion.app/tools/image_compressor_tool
https://www.filefusion.app/TOOLS/ImageCompressor
https://www.filefusion.app/tools/img-comp-tool-online-free-fast

<!-- Issues:
   - Not readable
   - Inconsistent casing
   - Too long
   - Underscores instead of hyphens --&gt;`} />

                <h3>Canonical URLs</h3>
                <CodeBlock language="html" code={`<!-- Prevent duplicate content issues --&gt;
<link rel="canonical" href="https://www.filefusion.app/tools/image-compressor">

<!-- Use when:
   - Same content accessible via multiple URLs
   - URL parameters (utm_source, etc.)
   - HTTP vs HTTPS versions
   - www vs non-www versions --&gt;`} />
            </div>

            <div className="content-section">
                <h2>Page Speed & HTML Optimization</h2>

                <h3>Fast-Loading HTML</h3>
                <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Page Title</title>
    
    <!-- Preconnect to external domains --&gt;
    <link rel="preconnect" href="https://fonts.googleapis.com">
    
    <!-- Critical CSS inline --&gt;
    <style>
        /* Critical above-the-fold CSS */
    </style>
    
    <!-- Defer non-critical CSS --&gt;
    <link rel="stylesheet" href="styles.css" media="print" onload="this.media='all'">
</head>
<body>
    <!-- Content --&gt;
    
    <!-- Defer JavaScript --&gt;
    <script src="script.js" defer></script>
    
    <!-- Lazy load images --&gt;
    <img src="image.jpg" loading="lazy" alt="Description">
</body>
</html>`} />
            </div>

            <div className="content-section">
                <h2>Mobile-Friendly HTML</h2>

                <CodeBlock language="html" code={`<!-- Viewport meta tag (essential for mobile) --&gt;
<meta name="viewport" content="width=device-width, initial-scale=1.0">

<!-- Mobile-friendly clickable elements --&gt;
<button style="min-height: 48px; min-width: 48px;">
    Click Me
</button>

<!-- Responsive images --&gt;
<img src="image-small.jpg"
     srcset="image-small.jpg 400w,
             image-medium.jpg 800w,
             image-large.jpg 1200w"
     sizes="(max-width: 600px) 400px,
            (max-width: 900px) 800px,
            1200px"
     alt="Responsive image">`} />
            </div>

            <div className="content-section">
                <h2>Complete SEO HTML Template</h2>

                <CodeBlock language="html" code={`<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    
    <!-- Essential SEO --&gt;
    <title>Free Image Compressor - Reduce JPG, PNG Size | FileFusion</title>
    <meta name="description" content="Compress images online for free. Reduce file sizes by up to 80% without losing quality. Fast, secure, no registration.">
    
    <!-- Canonical URL --&gt;
    <link rel="canonical" href="https://www.filefusion.app/tools/image-compressor">
    
    <!-- Open Graph (social media) --&gt;
    <meta property="og:title" content="Free Image Compressor | FileFusion">
    <meta property="og:description" content="Compress images by up to 80% without losing quality">
    <meta property="og:image" content="https://www.filefusion.app/images/og-image.jpg">
    <meta property="og:url" content="https://www.filefusion.app/tools/image-compressor">
    
    <!-- Favicon --&gt;
    <link rel="icon" type="image/png" href="/favicon.png">
</head>
<body>
    <header>
        <nav aria-label="Main navigation">
            <a href="/">FileFusion</a>
            <a href="/tools">Tools</a>
            <a href="/tutorials">Tutorials</a>
        </nav>
    </header>
    
    <main>
        <article>
            <h1>Free Online Image Compressor</h1>
            
            <section>
                <h2>How to Compress Images</h2>
                <p>Learn how to reduce image file sizes...</p>
            </section>
        </article>
    </main>
    
    <footer>
        <p>&copy; 2024 FileFusion. All rights reserved.</p>
    </footer>
</body>
</html>`} />
            </div>

        </div>
    );
};

export default HTMLSeoBasics;




